import {
  getBatterySignals,
  getPackComparisionV4,
} from "../../../Api/DigitalTwin";
import {
  convertAndGetTimeStampInRequiredFormat,
  convertZonetoUTCStart,
  convertZonetoUTCEnd,
} from "../../../Helper/DatePicker/DateConverter";
import { chartColorCodes } from "../../../Helper/DigitalTwin/DataFile";
import {
  formatBinarySegmentsForChart,
  formatDigitalTwinChartData,
  prepareSegmentsFromBinarySignalsData,
} from "../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { isValidData } from "../../../Helper/Filters/DataFilters";
import {
  availableBinarySignalColors,
  colorForNoDataBinarySignal,
} from "../../DigitalTwin/TabsSection/BatteryVitals/_Constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/**
 * Source of a chart annotation. BMS faults and Smart Alerts are normalized to
 * the same annotation shape and combined into one annotation array; this tag
 * lets the tooltip render the correct per-card source label, icon and accent.
 */
export const ANNOTATION_SOURCE = {
  BMS_FAULT: "bmsFault",
  SMART_ALERT: "smartAlert",
};

/**
 * Display config for an annotation source — the label + colors used by both the
 * annotation tooltip's source tag and the chart hover tooltip's source label.
 * BMS faults use teal, smart alerts use indigo. Unknown/legacy sources fall
 * back to BMS faults.
 */
export const ANNOTATION_SOURCE_DISPLAY = {
  [ANNOTATION_SOURCE.BMS_FAULT]: { label: "Battery System Fault", color: "#0F7B6C", bg: "#E3F2EF" },
  [ANNOTATION_SOURCE.SMART_ALERT]: { label: "Smart Alert", color: "#4D44D8", bg: "#ECEBFB" },
};

export const getAnnotationSourceDisplay = (source) =>
  ANNOTATION_SOURCE_DISPLAY[source] || ANNOTATION_SOURCE_DISPLAY[ANNOTATION_SOURCE.BMS_FAULT];

// Short form mapping
const shortForms = {
  batterysystem: "batsys",
  string: "str",
  pack: "p",
  module: "m",
  cell: "c",
  voltage: "voltage",
  temperature: "temperature",
  current: "current",
};

export const getShuffledColors = () => {
  let colors = [...chartColorCodes];
  for (let i = colors.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [colors[i], colors[j]] = [colors[j], colors[i]];
  }
  return colors;
};

export const transformTree = (input, levelSignals) => {
  // Helper: clean label (remove spaces)
  const cleanName = (label, spacer = "") =>
    label.trim().toLowerCase().replace(/\s+/g, spacer);

  // Turn levelSignals into an object for faster lookup
  const levelSignalMap = levelSignals.reduce((acc, item) => {
    const [level, signals] = Object.entries(item)[0];
    acc[level] = signals;
    return acc;
  }, {});

  // Build default children for a given node level
  const getDefaultChildren = (level) => {
    const signals = levelSignalMap[level] || [];
    return signals.reduce((map, signal) => {
      const cleanSignalLabel = cleanName(signal.label, "_");
      map[cleanSignalLabel] = {
        name: cleanSignalLabel,
        label: signal.label,
        type: "normal",
        unit: signal.unitOfMeasurement || "",
        key: signal.key,
        possibleValues: signal.possibleValues,
      };
      return map;
    }, {});
  };

  // Recursive function
  function transformNode(node, siblingsCount = 1) {
    const newNode = {
      name: cleanName(node.label),
      type: "complex",
      id: node.id,
    };

    // default children for this level
    let childrenMap = getDefaultChildren(node.level);

    // Merge actual children if any
    if (node.children && node.children.length > 0) {
      // First build all child nodes
      const grouped = {};
      node.children.forEach((child) => {
        const clean = cleanName(child.label);
        const childNode = transformNode(child, node.children.length);
        grouped[clean] = childNode;
      });

      // Check if children are of the same level
      const childLevels = node.children.map((c) => c.level);
      const uniqueLevels = [...new Set(childLevels)];

      uniqueLevels.forEach((lvl) => {
        const sameLevelChildren = node.children.filter((c) => c.level === lvl);
        if (sameLevelChildren.length > 1) {
          // Create all-node for this level
          const allNodeName = `${cleanName(lvl)}all`;
          const allChildren = getDefaultChildren(lvl);

          grouped[allNodeName] = {
            name: allNodeName,
            type: "complex",
            id: `${node.id}_${lvl}_all`,
            children: allChildren,
          };
        }
      });

      childrenMap = { ...childrenMap, ...grouped };
    }

    if (Object.keys(childrenMap).length > 0) {
      newNode.children = childrenMap;
    }

    return newNode;
  }

  // Root level (e.g. BATTERY_SYSTEM array)
  const result = {};
  Object.values(input).forEach((arr) => {
    arr.forEach((node) => {
      result[cleanName(node.label)] = transformNode(node, arr.length);
    });
  });

  return result;
};

/**
 * Transform events/alerts list into hierarchical structure
 * @param {Array} alertsList - Array of alerts with { id, value } structure
 * @param {string} rootName - Name of the root node (default: "bmsfaults")
 * @returns {Object} Hierarchical structure compatible with HierarchicalSelector
 */
export const transformEventsTree = (alertsList, rootName = "bmsfaults") => {
  const cleanName = (label, spacer = "") =>
    label.trim().toLowerCase().replace(/\s+/g, spacer);

  const transformedEventsHierarchy = {
    [rootName]: {
      name: rootName,
      type: "complex",
      id: `${rootName}_root`,
      children: {},
    },
  };

  // Add each alert as a child under the root
  alertsList.forEach((alert) => {
    const alertName = cleanName(alert.value, "_");
    transformedEventsHierarchy[rootName].children[alertName] = {
      name: alertName,
      label: alert.value,
      type: "normal",
      unit: "",
      key: alert.id,
      possibleValues: {},
    };
  });

  return transformedEventsHierarchy;
};

/**
 * Extract [{ type, id, metric }] from dot-paths using a hierarchical tree.
 * - `data` can be either a single root node ({ name, type, id, children }) or
 *   a map of roots ({ [rootName]: rootNode }).
 * - Always uses the node's real `id`; paths that don't resolve are skipped.
 */
export const extractEntities = (deviceId, paths, data) => {
  const results = [];

  const inferTypeFromName = (name) => {
    const base = name.replace(/\s+/g, "").replace(/\d+$/g, "").toLowerCase();
    return base === "batterysystem" ? "battery" : base; // normalize
  };

  const getChildByKey = (children, key) => {
    if (!children) return null;
    // exact match first
    if (children[key]) return children[key];
    // fallback: case-insensitive match
    const lower = key.toLowerCase();
    const k = Object.keys(children).find((c) => c.toLowerCase() === lower);
    return k ? children[k] : null;
  };

  const resolveRoot = (data, firstSegment) => {
    // Single root node shape
    if (
      data &&
      typeof data === "object" &&
      "name" in data &&
      "children" in data
    ) {
      return { root: data, startIndex: firstSegment === data.name ? 1 : 0 };
    }
    // Map of roots shape
    if (data && typeof data === "object") {
      if (data[firstSegment])
        return { root: data[firstSegment], startIndex: 1 };
      const keys = Object.keys(data);
      if (keys.length === 1) {
        const only = data[keys[0]];
        const start =
          firstSegment === only?.name || firstSegment === keys[0] ? 1 : 0;
        return { root: only, startIndex: start };
      }
    }
    return { root: null, startIndex: 0 };
  };

  for (const rawPath of paths) {
    const parts = rawPath
      .split(".")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length < 2) continue;

    const metricName = parts[parts.length - 1]; // actual metric key
    const { root, startIndex } = resolveRoot(data, parts[0]);
    if (!root) continue;

    // Traverse complex nodes until just before metric
    let node = root;
    let ok = true;
    for (let i = startIndex; i < parts.length - 1; i++) {
      const seg = parts[i];
      const next = getChildByKey(node.children, seg);
      if (!next || next.type !== "complex") {
        ok = false;
        break;
      }
      node = next;
    }
    if (!ok || !node || !node.id) continue;

    // Now explicitly get the metric child (type=normal)
    const metricNode = getChildByKey(node.children, metricName);
    if (!metricNode || metricNode.type !== "normal") continue;

    results.push({
      type: inferTypeFromName(node.name),
      id: node.id,
      metric: metricNode.key ?? metricNode.name ?? metricName.toLowerCase(),
      label: metricNode.label,
      raw: rawPath,
      shortRaw: getChipShortForm(rawPath),
      deviceId: deviceId,
      unit: metricNode.unit || "", // correct place to pull unit
      possibleValues: metricNode?.possibleValues || {},
    });
  }

  return results;
};

export const transformResponse = (apiResponse, entity) => {
  return apiResponse.map((row) => ({
    batteryDateTime: row.batteryDateTime,
    metricsArray: row.metricsArray.map((m) => ({
      ...m,
      type: entity.raw,
    })),
  }));
};

export const mergeResponses = (allResponses) => {
  const mergedMap = new Map();

  for (const response of allResponses) {
    for (const row of response) {
      if (!mergedMap.has(row.batteryDateTime)) {
        mergedMap.set(row.batteryDateTime, {
          batteryDateTime: row.batteryDateTime,
          metricsArray: [],
        });
      }
      mergedMap.get(row.batteryDateTime).metricsArray.push(...row.metricsArray);
    }
  }

  // Convert map back to sorted array
  return Array.from(mergedMap.values()).sort(
    (a, b) => new Date(a.batteryDateTime) - new Date(b.batteryDateTime),
  );
};

export const resetContinuousChartData = async (
  chartEntities,
  startDate,
  endDate,
  intervalSeconds = 300,
) => {
  const promises = chartEntities.map(async (entity) => {
    const res = await getPackComparisionV4(
      entity.deviceId,
      entity.id,
      entity.type,
      [entity.metric],
      [startDate, endDate],
      intervalSeconds,
    );
    const responseData = res?.response?.batteryData || [];
    return transformResponse(responseData, entity); // decorate with type
  });

  const allResponses = await Promise.all(promises);
  const mergedResponses = mergeResponses(allResponses);

  const entityKeys = chartEntities.map((entity) => entity.raw);
  let metricsData = {};

  for (let i in entityKeys) {
    let metricKey = entityKeys[i];
    metricsData[metricKey] = formatDigitalTwinChartData(
      metricKey,
      mergedResponses,
    );
  }

  let seriesData;

  const signals = chartEntities.map((entity) => {
    const key = entity.raw;
    const shortKey = entity.shortRaw;
    const name = entity.unit ? `${shortKey} (${entity.unit})` : shortKey;
    return {
      key: key,
      label: name,
      name: name,
      unit: entity.unit,
      yAxisDecimalPlaces: 0,
      ranges: {},
      color: entity.color,
      yaxis: entity.yaxis,
      group: `y-axis-${entity.yaxis}`,
    };
  });

  seriesData = signals.map((metric) => ({
    ...metric,
    data: metricsData[metric.key],
  }));

  return {
    metrics: signals,
    series: seriesData,
    isValid: true,
    configs: {
      secondaryGranularity: "avg", // hardcoded need to replace with existing selection
    },
  };
};

export const extractUniqueLevels = (data) => {
  const levels = new Set();

  function traverse(nodes) {
    nodes.forEach((node) => {
      if (node.level) {
        levels.add(node.level);
      }
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  }

  // Root level (e.g. BATTERY_SYSTEM array)
  Object.values(data).forEach((arr) => {
    traverse(arr);
  });
  return Array.from(levels);
};

export const fetchDigitalTwinHierarchyLevelSignals = async (
  batteryID,
  levels,
  type = "CONTINUOUS", // or 'BINARY'
) => {
  try {
    const promises = levels.map(async (level) => {
      const batterySignalsResponse = await getBatterySignals(batteryID, level);
      const allSignals = batterySignalsResponse.response?.data?.signals || [];
      let filteredSignals = allSignals.filter(
        (signal) => signal.signalType.toUpperCase() === type,
      );

      // TODO - hardcoding for temperature - to be removed once addressed from backend
      if (type === "CONTINUOUS") {
        filteredSignals = filteredSignals.map((signal) => ({
          ...signal,
          unitOfMeasurement:
            signal?.unitOfMeasurement === "C"
              ? "°C"
              : signal?.unitOfMeasurement,
        }));
      }
      return {
        [level]: filteredSignals,
      };
    });

    const allSignalsResponses = await Promise.all(promises);
    return allSignalsResponses;
  } catch (error) {
    console.error("Error fetching digital twin hierarchy:", error);
  }
};

export const resetBinaryChartData = async (chartEntities, selectedDate) => {
  const promises = chartEntities.map(async (entity) => {
    let entityType = entity.type;
    if (entityType === "bus") {
      entityType = "vehicle";
    }

    const res = await getPackComparisionV4(
      entity.deviceId,
      entity.id,
      entityType,
      [entity.metric],
      [selectedDate, selectedDate],
      300,
    );
    const rawBatteryResponseData = res?.response?.batteryData || [];
    const responseData = rawBatteryResponseData.map((item) => {
      return {
        ...item,
        batteryDateTime: convertAndGetTimeStampInRequiredFormat(
          item.batteryDateTime,
        ),
      };
    });
    return transformResponse(responseData, entity); // decorate with type
  });

  const allResponses = await Promise.all(promises);
  const binarySignalsData = mergeResponses(allResponses);

  const segments = prepareSegmentsFromBinarySignalsData(binarySignalsData);
  const finalPreparedSignals = {};
  chartEntities.forEach((entity) => {
    const possibleValues = Object.values(entity.possibleValues);
    const statusColorMap = {};
    possibleValues.forEach((status, index) => {
      statusColorMap[status] =
        availableBinarySignalColors[index % availableBinarySignalColors.length];
    });
    statusColorMap["noData"] = colorForNoDataBinarySignal;
    finalPreparedSignals[entity.raw] = {
      title: entity.shortRaw,
      segments: formatBinarySegmentsForChart(
        segments[entity.raw],
        selectedDate,
      ),
      colors: statusColorMap,
    };
  });
  return finalPreparedSignals;
};

export const getChipShortForm = (path) => {
  const parts = path.split(".");
  return parts
    .map((part) => {
      for (const [key, short] of Object.entries(shortForms)) {
        // important to check for "key_" so that it does not slice an attribute like "cell_number_of_max_cell_voltage"
        // with "c_number_of_max_cell_voltage" as short for cell is "c"
        if (part.startsWith(key) && !part.startsWith(`${key}_`))
          return short + part.slice(key.length);
      }
      return part;
    })
    .join(".");
};

/**
 * Calculate the grouping interval for annotations based on the visible time window
 * This is used to group nearby annotations together when zoomed out
 * @param {number} minX - Start timestamp in milliseconds
 * @param {number} maxX - End timestamp in milliseconds
 * @returns {number} Grouping interval in seconds
 */
export const calculateAnnotationGroupingInterval = (minX, maxX) => {
  // minX and maxX are timestamps in milliseconds
  const timeWindowMs = maxX - minX;

  // Convert to minutes for easier comparison
  const timeWindowMinutes = timeWindowMs / (1000 * 60);

  // Based on the grouping table:
  // <= 5 minutes -> 5s grouping
  if (timeWindowMinutes <= 5) {
    return 5;
  }
  // 5 - 30 minutes -> 30s grouping
  else if (timeWindowMinutes > 5 && timeWindowMinutes <= 30) {
    return 30;
  }
  // 30 min - 1 hour -> 1m (60s) grouping
  else if (timeWindowMinutes > 30 && timeWindowMinutes <= 60) {
    return 60;
  }
  // 1 - 6 hours -> 5m (300s) grouping
  else if (timeWindowMinutes > 60 && timeWindowMinutes <= 6 * 60) {
    return 300;
  }
  // 6 - 12 hours -> 15m (900s) grouping
  else if (timeWindowMinutes > 6 * 60 && timeWindowMinutes <= 12 * 60) {
    return 900;
  }
  // 12 - 24 hours -> 30m (1800s) grouping
  else if (timeWindowMinutes > 12 * 60 && timeWindowMinutes <= 24 * 60) {
    return 1800;
  }
  // Default fallback for anything larger than 24 hours
  else {
    return 1800;
  }
};

export const buildSeverityIntervals = (events) => {
  if (!events?.length) return [];

  // 1. Sort events by time (stable for same timestamp)
  const sortedEvents = [...events].sort((a, b) => {
    if (a.time !== b.time) return a.time - b.time;
    // start before end at same timestamp
    return a.type === "start" && b.type === "end" ? -1 : 1;
  });

  // 2. Collect unique ordered timestamps
  const timestamps = [...new Set(sortedEvents.map((e) => e.time))];

  const activeAlerts = new Map(); // key -> alert
  const result = [];

  // helper to get highest severity from active alerts (uses exported getSeverityConfig)
  const getHighestSeverity = () => {
    let highest = null;

    for (const alert of activeAlerts.values()) {
      const cfg = getSeverityConfig(alert.annotation.severity);
      if (!highest || cfg.priority > highest.priority) {
        highest = cfg;
      }
    }

    return highest;
  };

  // 3. Walk timeline
  for (let i = 0; i < timestamps.length; i++) {
    const currentTime = timestamps[i];
    const nextTime = timestamps[i + 1];

    // apply all events at currentTime
    sortedEvents
      .filter((e) => e.time === currentTime)
      .forEach((event) => {
        const key = `${event.annotation.customerDBTimeInsert}-${event.annotation.incidentErrorName}`;

        if (event.type === "start") {
          activeAlerts.set(key, event);
        } else {
          activeAlerts.delete(key);
        }
      });

    if (!nextTime) break;

    const highestSeverity = getHighestSeverity();
    if (!highestSeverity) continue;

    result.push({
      x1: currentTime,
      x2: nextTime,
      fillColor: highestSeverity.fillColor,
    });
  }

  // 4. Handle open-ended interval (still active alerts)
  const highestSeverity = getHighestSeverity();
  if (highestSeverity) {
    result.push({
      x1: timestamps[timestamps.length - 1],
      x2: Date.now(), // current_time
      fillColor: highestSeverity.fillColor,
    });
  }

  return result;
};

export const renderCountBadge = (count) => {
  if (!count || count <= 1) return "";

  const displayText = count > 99 ? "99+" : String(count);
  const fontSize = displayText.length > 2 ? 10 : 11;

  return `
    <circle cx="23" cy="7" r="7" fill="#FF4444"/>
    <text
      x="23"
      y="10"
      text-anchor="middle"
      font-size="${fontSize}"
      font-weight="700"
      fill="#FFFFFF"
      font-family="Arial, sans-serif">
      ${displayText}
    </text>
  `;
};

export const getCriticalIconSvg = (count) => `
<svg width="30" height="30" viewBox="0 0 30 30" fill="none"
     xmlns="http://www.w3.org/2000/svg">

  <circle cx="13.5" cy="13.5" r="13.5" fill="#FFE5E5"/>

  <path fill-rule="evenodd" clip-rule="evenodd"
    d="M15.5257 6.01554C14.7957 4.66149 12.9531 4.66149 12.2229 6.01554L6.25342 17.0854C5.52883 18.4289 6.44443 20.1008 7.90488 20.1008H19.8438C21.3042 20.1008 22.2199 18.4289 21.4954 17.0854L15.5257 6.01554Z"
    fill="#930500"/>

  <path fill-rule="evenodd" clip-rule="evenodd"
    d="M15.0636 11.0312C15.0636 10.3355 14.5314 9.77148 13.8748 9.77148C13.2183 9.77148 12.686 10.3355 12.686 11.0312V13.0467C12.686 13.7425 13.2183 14.3064 13.8748 14.3064C14.5314 14.3064 15.0636 13.7425 15.0636 13.0467V11.0312ZM13.8748 18.3375C14.5314 18.3375 15.0636 17.7734 15.0636 17.0778C15.0636 16.382 14.5314 15.8181 13.8748 15.8181C13.2183 15.8181 12.686 16.382 12.686 17.0778C12.686 17.7734 13.2183 18.3375 13.8748 18.3375Z"
    fill="#FFE5E5"/>

  ${renderCountBadge(count)}
</svg>
`;

export const getHighSeverityIconSvg = (count) => `
<svg width="30" height="30" viewBox="0 0 30 30" fill="none"
     xmlns="http://www.w3.org/2000/svg">

  <circle cx="13.5" cy="13.5" r="13.5" fill="#FFE9DB"/>

  <path fill-rule="evenodd" clip-rule="evenodd"
    d="M15.5257 6.01554C14.7957 4.66149 12.9531 4.66149 12.2229 6.01554L6.25342 17.0854C5.52883 18.4289 6.44443 20.1008 7.90488 20.1008H19.8438C21.3042 20.1008 22.2199 18.4289 21.4954 17.0854L15.5257 6.01554Z"
    fill="#FF6B0F"/>

  <path fill-rule="evenodd" clip-rule="evenodd"
    d="M15.0636 11.0312C15.0636 10.3355 14.5314 9.77148 13.8748 9.77148C13.2183 9.77148 12.686 10.3355 12.686 11.0312V13.0467C12.686 13.7425 13.2183 14.3064 13.8748 14.3064C14.5314 14.3064 15.0636 13.7425 15.0636 13.0467V11.0312ZM13.8748 18.3375C14.5314 18.3375 15.0636 17.7734 15.0636 17.0778C15.0636 16.382 14.5314 15.8181 13.8748 15.8181C13.2183 15.8181 12.686 16.382 12.686 17.0778C12.686 17.7734 13.2183 18.3375 13.8748 18.3375Z"
    fill="#FFE9DB"/>

  ${renderCountBadge(count)}
</svg>
`;

export const getWarningIconSvg = (count) => `
<svg width="30" height="30" viewBox="0 0 30 30" fill="none"
     xmlns="http://www.w3.org/2000/svg">

  <circle cx="13.5" cy="13.5" r="13.5" fill="#FFF6D7"/>

  <path fill-rule="evenodd" clip-rule="evenodd"
    d="M15.5257 6.01554C14.7957 4.66149 12.9531 4.66149 12.2229 6.01554L6.25342 17.0854C5.52883 18.4289 6.44443 20.1008 7.90488 20.1008H19.8438C21.3042 20.1008 22.2199 18.4289 21.4954 17.0854L15.5257 6.01554Z"
    fill="#DCA101"/>

  <path fill-rule="evenodd" clip-rule="evenodd"
    d="M15.0636 11.0312C15.0636 10.3355 14.5314 9.77148 13.8748 9.77148C13.2183 9.77148 12.686 10.3355 12.686 11.0312V13.0467C12.686 13.7425 13.2183 14.3064 13.8748 14.3064C14.5314 14.3064 15.0636 13.7425 15.0636 13.0467V11.0312ZM13.8748 18.3375C14.5314 18.3375 15.0636 17.7734 15.0636 17.0778C15.0636 16.382 14.5314 15.8181 13.8748 15.8181C13.2183 15.8181 12.686 16.382 12.686 17.0778C12.686 17.7734 13.2183 18.3375 13.8748 18.3375Z"
    fill="#FFF6D7"/>

  ${renderCountBadge(count)}
</svg>
`;

export const getResolvedIconSvg = (count) => `
<svg width="30" height="30" viewBox="0 0 30 30" fill="none"
     xmlns="http://www.w3.org/2000/svg">

  <circle cx="13.5" cy="13.5" r="13.5" fill="#EEF4DB"/>

  <path
    d="M8.44934 14.5648L11.7041 17.7545L18.9707 10.6328"
    stroke="#008A15"
    stroke-width="1.81486"
    stroke-linecap="round"
    stroke-linejoin="round"/>

  ${renderCountBadge(count)}
</svg>
`;

export const svgToDataUrl = (svg) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

export const getIncidentIconSvg = ({ severity, count }) => {
  switch (severity) {
    case "critical":
      return getCriticalIconSvg(count);

    case "high severity":
      return getHighSeverityIconSvg(count);

    case "warning":
      return getWarningIconSvg(count);

    case "resolved":
      return getResolvedIconSvg(count);

    default:
      return getWarningIconSvg(count); // safe fallback
  }
};

// ============================================================================
// CHART CONFIGURATION CONSTANTS
// ============================================================================

export const CHART_CONSTANTS = {
  BUFFER_PERCENT: 0.1,
  DEFAULT_Y_MIN: 0,
  DEFAULT_Y_MAX: 1,
  ANNOTATION_AXIS_MIN: 0,
  ANNOTATION_AXIS_MAX: 100,
  ICON_SIZE: 25,
  ICON_OFFSET_Y: 10,
  TOOLTIP_SPACING: 10,
  TOOLTIP_SETUP_DELAY: 100,
};

// ============================================================================
// SHARED CHART UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate min/max values for Y1 and Y2 axes with buffer
 * @param {Array} series - Chart series data
 * @param {number} bufferPercent - Percentage buffer to add (default 0.1 = 10%)
 * @returns {Object} { y1AxisMin, y1AxisMax, y2AxisMin, y2AxisMax }
 */
export const calculateMinMaxForAxis = (
  series,
  bufferPercent = CHART_CONSTANTS.BUFFER_PERCENT,
) => {
  let y1Values = [];
  let y2Values = [];

  series.forEach((s) => {
    if (!s?.data?.length) return;

    const values = s.data
      .filter((d) => isValidData(d.y))
      .map((d) => Number(d.y))
      .filter((v) => !isNaN(v));

    if (s.yaxis === "Y1") {
      y1Values.push(...values);
    } else if (s.yaxis === "Y2") {
      y2Values.push(...values);
    }
  });

  // Default to 0-1 if empty (so chart won't crash)
  const y1Min = y1Values.length
    ? Math.min(...y1Values)
    : CHART_CONSTANTS.DEFAULT_Y_MIN;
  const y1Max = y1Values.length
    ? Math.max(...y1Values)
    : CHART_CONSTANTS.DEFAULT_Y_MAX;
  const y2Min = y2Values.length
    ? Math.min(...y2Values)
    : CHART_CONSTANTS.DEFAULT_Y_MIN;
  const y2Max = y2Values.length
    ? Math.max(...y2Values)
    : CHART_CONSTANTS.DEFAULT_Y_MAX;

  // Apply buffer (10% by default)
  const applyBuffer = (min, max) => {
    if (min === max) {
      // handle flat line case
      return { min: min - 1, max: max + 1 };
    }
    const range = max - min;
    const padding = range * bufferPercent;
    return {
      min: Math.floor(min - padding),
      max: Math.ceil(max + padding),
    };
  };

  const y1 = applyBuffer(y1Min, y1Max);
  const y2 = applyBuffer(y2Min, y2Max);

  return {
    y1AxisMin: y1.min,
    y1AxisMax: y1.max,
    y2AxisMin: y2.min,
    y2AxisMax: y2.max,
  };
};

/**
 * Get the grouping interval for annotations based on zoom level
 * @param {Object} zoomedRange - { minX, maxX } or null
 * @param {Object} startDate - { value: dateString } or null
 * @returns {number} Grouping interval in milliseconds
 */
export const getAnnotationGroupingInterval = (zoomedRange, startDate) => {
  // Get current zoom range, or use the default full-day range
  const minX =
    zoomedRange?.minX || new Date(startDate?.value || new Date()).getTime();
  const maxX =
    zoomedRange?.maxX ||
    new Date(startDate?.value || new Date()).getTime() + 86400000;

  // Calculate the grouping interval in seconds based on visible time window
  const intervalSeconds = calculateAnnotationGroupingInterval(minX, maxX);

  // Convert to milliseconds for grouping
  return intervalSeconds * 1000;
};

/**
 * Find the index of the annotations axis in the Y-axis settings
 * @param {Array} yAxisSettings - Array of Y-axis configurations
 * @returns {number} Index of the annotations axis
 */
export const getAnnotationAxisIndex = (yAxisSettings) => {
  // Defensive check for empty or undefined yAxisSettings
  if (!yAxisSettings || yAxisSettings.length === 0) {
    console.warn(
      "No Y-axis settings found, using default index 0 for annotations",
    );
    return 0;
  }

  const annotationAxisIndex = yAxisSettings.findIndex(
    (axis) => axis?.seriesName === "annotations-axis",
  );
  return annotationAxisIndex !== -1
    ? annotationAxisIndex
    : yAxisSettings.length - 1;
};

/**
 * Get the max value from the annotations axis
 * @param {Array} yAxisSettings - Array of Y-axis configurations
 * @param {number} annotationAxisIndex - Index of the annotations axis
 * @returns {number} Max value for the annotations axis
 */
export const getAnnotationYMax = (yAxisSettings, annotationAxisIndex) => {
  // Defensive check
  if (!yAxisSettings || annotationAxisIndex >= yAxisSettings.length) {
    console.warn("Invalid annotation axis index, using default max value 100");
    return CHART_CONSTANTS.ANNOTATION_AXIS_MAX;
  }

  const annotationAxis = yAxisSettings[annotationAxisIndex];
  return annotationAxis?.max || CHART_CONSTANTS.ANNOTATION_AXIS_MAX;
};

/**
 * Position tooltip with boundary detection to prevent overflow
 * @param {HTMLElement} tooltip - Tooltip DOM element
 * @param {DOMRect} rect - Bounding rectangle of the target element
 */
export const positionTooltip = (tooltip, rect) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const tooltipWidth = tooltip.offsetWidth;
  const tooltipHeight = tooltip.offsetHeight;
  const spacing = CHART_CONSTANTS.TOOLTIP_SPACING;

  let left = rect.left + window.scrollX;
  let top = rect.bottom + window.scrollY + spacing;

  // Check if tooltip overflows right edge
  if (left + tooltipWidth > viewportWidth) {
    left = rect.right + window.scrollX - tooltipWidth;
  }

  // Ensure tooltip doesn't overflow left edge
  if (left < 0) {
    left = spacing;
  }

  // Check if tooltip overflows bottom edge
  if (rect.bottom + tooltipHeight + spacing > viewportHeight) {
    top = rect.top + window.scrollY - tooltipHeight - spacing;
  }

  // Ensure tooltip doesn't overflow top edge
  if (top < 0) {
    top = rect.bottom + window.scrollY + spacing;
  }

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
};

/**
 * Get severity configuration for annotations
 * @param {string} severity - Severity level (critical, high severity, warning)
 * @returns {Object} { fillColor, priority, severity }
 */
export const getSeverityConfig = (severity) => {
  const severityKey = severity?.toLowerCase().trim() || "warning";

  if (severityKey === "critical") {
    return {
      fillColor: "#FFE5E5",
      priority: 3,
      severity: severityKey,
    };
  } else if (severityKey === "high severity") {
    return {
      fillColor: "#FFE9DB",
      priority: 2,
      severity: severityKey,
    };
  } else {
    return {
      fillColor: "#FFF6D7",
      priority: 1,
      severity: severityKey,
    };
  }
};

/**
 * Generate annotation tooltip HTML (works with both renderToStaticMarkup and renderToString)
 * @param {Object} timePoint - Time point object containing startAnnotations and endAnnotations
 * @param {Function} AnnotationTooltipComponent - React component for rendering tooltip
 * @param {Function} renderFunction - ReactDOMServer.renderToStaticMarkup or renderToString
 * @returns {string} HTML string for tooltip
 */
export const getAnnotationTooltipHtml = (
  timePoint,
  AnnotationTooltipComponent,
  renderFunction,
) => {
  // Pass the full timePoint object to the component
  // The timePoint contains startAnnotations, endAnnotations, and other metadata
  return renderFunction(AnnotationTooltipComponent({ timePoint: timePoint }));
};

/**
 * Group annotations into markers for rendering using sliding-window (gap-based)
 * clustering. Each annotation contributes a "start" time-point at its activation
 * and, if resolved, an "end" time-point at its resolution. All time-points are
 * sorted and clustered: consecutive points whose gap is <= groupingInterval are
 * merged into one marker. Unlike fixed-grid bucketing, this has no boundary
 * artifact — any two events closer than the threshold always merge, regardless
 * of their absolute position on the timeline.
 * @param {Array} annotations - Array of annotation objects
 * @param {number} groupingInterval - Max gap (ms) between consecutive points to merge
 * @returns {Array} Array of { timestamp, annotations, type, severityConfig, count, uniqueCount, startAnnotations, endAnnotations }
 */
export const groupAnnotationsForRendering = (annotations, groupingInterval) => {
  if (!annotations || annotations.length === 0) {
    return [];
  }

  // Step 1: expand annotations into individual time-points (start + optional end).
  const points = [];
  annotations.forEach((annotation) => {
    const activatedTime = new Date(annotation.customerDBTimeInsert).getTime();
    if (!isNaN(activatedTime)) {
      points.push({ time: activatedTime, kind: "start", annotation });
    }
    if (annotation.resolveCustomerDBTime) {
      const resolvedTime = new Date(annotation.resolveCustomerDBTime).getTime();
      if (!isNaN(resolvedTime)) {
        points.push({ time: resolvedTime, kind: "end", annotation });
      }
    }
  });

  if (points.length === 0) {
    return [];
  }

  // Step 2: sort by time and cluster consecutive points within the gap threshold.
  points.sort((a, b) => a.time - b.time);

  const clusters = [];
  let current = null;
  for (const point of points) {
    if (current && point.time - current.lastTime <= groupingInterval) {
      current.points.push(point);
      current.lastTime = point.time;
    } else {
      current = { points: [point], firstTime: point.time, lastTime: point.time };
      clusters.push(current);
    }
  }

  // Step 3: convert clusters into the timePoint shape consumed by the marker /
  // tooltip / fill-region code.
  return clusters.map((cluster) => {
    const startAnnotations = cluster.points
      .filter((p) => p.kind === "start")
      .map((p) => p.annotation);
    const endAnnotations = cluster.points
      .filter((p) => p.kind === "end")
      .map((p) => p.annotation);

    const hasStartAnnotations = startAnnotations.length > 0;
    const type = hasStartAnnotations ? "start" : "end";

    const allAnnotations = [...startAnnotations, ...endAnnotations];

    const uniqueAnnotations = new Set();
    allAnnotations.forEach((annotation) => {
      uniqueAnnotations.add(annotation.incidentID);
    });
    const uniqueCount = uniqueAnnotations.size;

    // Icon severity comes from the start annotations when present, else the end ones.
    const annotationsForIcon = hasStartAnnotations ? startAnnotations : endAnnotations;
    const highestSeverityAnnotation = annotationsForIcon.reduce(
      (highest, current) => {
        const currentPriority = getSeverityConfig(current.severity).priority;
        const highestPriority = getSeverityConfig(highest.severity).priority;
        return currentPriority > highestPriority ? current : highest;
      },
    );

    return {
      // Marker sits at the cluster's earliest event.
      timestamp: cluster.firstTime,
      annotations: allAnnotations,
      type: type,
      count: allAnnotations.length,
      uniqueCount: uniqueCount,
      severityConfig: getSeverityConfig(highestSeverityAnnotation.severity),
      startAnnotations: startAnnotations,
      endAnnotations: endAnnotations,
    };
  });
};

/**
 * Setup custom tooltips for annotation points
 * @param {Object} chartContext - ApexCharts chart context
 * @param {Array} annotationData - Array of annotation objects
 * @param {Function} groupingFunction - Function to group annotations
 * @param {Function} tooltipHtmlFunction - Function to generate tooltip HTML
 * @returns {Function} Cleanup function to remove tooltips and event listeners
 */
export const setupAnnotationTooltips = (
  chartContext,
  annotationData,
  groupingFunction,
  tooltipHtmlFunction,
) => {
  const tooltipRefs = [];
  let timeoutId;
  let pinnedTooltip = null; // Track currently pinned tooltip

  // Document click handler to close pinned tooltip when clicking outside
  const documentClickHandler = (e) => {
    if (pinnedTooltip) {
      // Check if click is outside the pinned tooltip and its associated point
      const clickedOnPoint = tooltipRefs.some(
        ({ point }) => point && point.contains(e.target),
      );
      const clickedOnTooltip = pinnedTooltip.contains(e.target);

      if (!clickedOnPoint && !clickedOnTooltip) {
        pinnedTooltip.style.display = "none";
        pinnedTooltip = null;
      }
    }
  };

  const cleanup = () => {
    // Clear timeout if still pending
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Remove document click listener
    document.removeEventListener("click", documentClickHandler);

    // Remove all event listeners and tooltips
    tooltipRefs.forEach(
      ({
        tooltip,
        point,
        mouseenterHandler,
        mouseleaveHandler,
        clickHandler,
      }) => {
        if (point) {
          point.removeEventListener("mouseenter", mouseenterHandler);
          point.removeEventListener("mouseleave", mouseleaveHandler);
          point.removeEventListener("click", clickHandler);
        }
        if (tooltip && tooltip.parentNode) {
          tooltip.remove();
        }
      },
    );
    tooltipRefs.length = 0;
    pinnedTooltip = null;
  };

  timeoutId = setTimeout(() => {
    // Clean up old tooltips first
    const oldTooltips = document.querySelectorAll(".annotation-custom-tooltip");
    oldTooltips.forEach((tooltip) => tooltip.remove());

    const chartElement = chartContext.el;
    if (!chartElement || !annotationData || annotationData.length === 0) return;

    // Find all annotation point images
    const annotationPoints = chartElement.querySelectorAll(
      ".apexcharts-point-annotations image",
    );

    // Get all grouped time points
    const groupedTimePoints = groupingFunction(annotationData);

    // Add tooltips for all time points
    groupedTimePoints.forEach((timePoint, index) => {
      if (annotationPoints[index]) {
        const point = annotationPoints[index];
        const tooltip = document.createElement("div");
        tooltip.className = "annotation-custom-tooltip";
        // Pass the full timePoint object instead of just annotations
        // This allows the tooltip to access startAnnotations and endAnnotations separately
        tooltip.innerHTML = tooltipHtmlFunction(timePoint);
        tooltip.style.display = "none";
        tooltip.style.position = "absolute";
        tooltip.style.zIndex = "9999";
        tooltip.style.pointerEvents = "none";
        document.body.appendChild(tooltip);

        const mouseenterHandler = (e) => {
          // Don't show on hover if another tooltip is pinned
          if (pinnedTooltip && pinnedTooltip !== tooltip) return;
          const rect = e.target.getBoundingClientRect();
          tooltip.style.display = "block";
          positionTooltip(tooltip, rect);
        };

        const mouseleaveHandler = () => {
          // Don't hide if this tooltip is pinned
          if (pinnedTooltip === tooltip) return;
          tooltip.style.display = "none";
        };

        const clickHandler = (e) => {
          e.stopPropagation();
          // If clicking on same point that's already pinned, unpin it
          if (pinnedTooltip === tooltip) {
            pinnedTooltip = null;
            tooltip.style.display = "none";
          } else {
            // Hide previously pinned tooltip
            if (pinnedTooltip) {
              pinnedTooltip.style.display = "none";
            }
            // Pin this tooltip
            pinnedTooltip = tooltip;
            const rect = e.target.getBoundingClientRect();
            tooltip.style.display = "block";
            positionTooltip(tooltip, rect);
          }
        };

        point.addEventListener("mouseenter", mouseenterHandler);
        point.addEventListener("mouseleave", mouseleaveHandler);
        point.addEventListener("click", clickHandler);
        point.style.cursor = "pointer";

        // Store references for cleanup
        tooltipRefs.push({
          tooltip,
          point,
          mouseenterHandler,
          mouseleaveHandler,
          clickHandler,
        });
      }
    });

    // Add document click listener to close pinned tooltip when clicking outside
    document.addEventListener("click", documentClickHandler);
  }, CHART_CONSTANTS.TOOLTIP_SETUP_DELAY);

  // Return cleanup function
  return cleanup;
};

/**
 * Filter annotations based on zoom window
 * @param {Array} annotationData - Array of annotation objects
 * @param {Object} zoomedRange - { minX, maxX } zoom range
 * @returns {Array} Filtered annotations that intersect with zoom window
 */
export const filterAnnotationsByZoom = (annotationData, zoomedRange) => {
  if (!annotationData || annotationData.length === 0) {
    return [];
  }

  if (!zoomedRange || !zoomedRange.minX || !zoomedRange.maxX) {
    return annotationData;
  }

  return annotationData.filter((annotation) => {
    const activatedTime = new Date(annotation.customerDBTimeInsert).getTime();
    const resolvedTime = annotation.resolveCustomerDBTime
      ? new Date(annotation.resolveCustomerDBTime).getTime()
      : null;

    const startInRange =
      activatedTime >= zoomedRange.minX && activatedTime <= zoomedRange.maxX;
    const endInRange =
      resolvedTime &&
      resolvedTime >= zoomedRange.minX &&
      resolvedTime <= zoomedRange.maxX;
    const spansRange =
      activatedTime < zoomedRange.minX &&
      resolvedTime &&
      resolvedTime > zoomedRange.maxX;

    return startInRange || endInRange || spansRange;
  });
};

/**
 * Get visible time points (grouped and filtered by zoom window)
 * @param {Array} filteredAnnotations - Pre-filtered annotations
 * @param {Function} groupingFunction - Function to group annotations
 * @param {Object} zoomedRange - { minX, maxX } zoom range
 * @param {Object} startDate - { value: dateString } for unzoomed state (format: "YYYY-MM-DD")
 * @returns {Array} Time points that are visible in the zoom window
 */
export const getVisibleTimePointsForZoom = (
  filteredAnnotations,
  groupingFunction,
  zoomedRange,
  startDate = null,
) => {
  const allGroupedPoints = groupingFunction(filteredAnnotations);
  // Filter to only time points within zoom window
  if (zoomedRange && zoomedRange.minX && zoomedRange.maxX) {
    return allGroupedPoints.filter(
      (tp) =>
        tp.timestamp >= zoomedRange.minX && tp.timestamp <= zoomedRange.maxX,
    );
  }

  // When zoom is not applied, filter based on startDate's day range
  if (startDate?.value) {
    const startDateUTC = convertZonetoUTCStart(startDate.value);
    const endDateUTC = convertZonetoUTCEnd(startDate.value);
    // Parse UTC strings as UTC
    const minX = dayjs.utc(startDateUTC, "YYYY-MM-DD HH:mm:ss").valueOf();
    const maxX = dayjs.utc(endDateUTC, "YYYY-MM-DD HH:mm:ss").valueOf();

    return allGroupedPoints.filter(
      (tp) => tp.timestamp >= minX && tp.timestamp <= maxX,
    );
  }

  return allGroupedPoints;
};

/**
 * Get boundary time points for the visible range (always includes start and end of range)
 * This ensures fill regions are rendered even when no annotation markers are visible
 * @param {Object} zoomedRange - { minX, maxX } zoom range
 * @param {Object} startDate - { value: dateString } for unzoomed state (format: "YYYY-MM-DD")
 * @returns {Array} Array with two timestamps [minX, maxX] representing the visible range boundaries in UTC
 */
export const getBoundaryTimePoints = (zoomedRange, startDate) => {
  // If zoomed, use the zoomed range directly (already in UTC milliseconds)
  if (zoomedRange?.minX && zoomedRange?.maxX) {
    return [zoomedRange.minX, zoomedRange.maxX];
  }

  // For unzoomed state, convert the startDate from user's timezone to UTC
  // startDate.value is in format "YYYY-MM-DD" in user's timezone
  const startDateUTC = convertZonetoUTCStart(startDate?.value);
  const endDateUTC = convertZonetoUTCEnd(startDate?.value);

  // Convert UTC date strings to milliseconds timestamps
  // The convertZonetoUTC functions return UTC strings in "YYYY-MM-DD HH:mm:ss" format
  // We need to parse them as UTC, not local time, so we use dayjs.utc()
  const minX = dayjs.utc(startDateUTC, "YYYY-MM-DD HH:mm:ss").valueOf();
  const maxX = dayjs.utc(endDateUTC, "YYYY-MM-DD HH:mm:ss").valueOf();

  return [minX, maxX];
};

/**
 * Build time events from annotations for severity fill regions
 * Uses ALL annotations (not filtered) to determine severity fill regions
 * @param {Array} allAnnotations - All annotations (not filtered by zoom)
 * @param {Array} boundaryTimePoints - [minX, maxX] of the visible range
 * @returns {Array} Time events with clamped timestamps, sorted by time
 */
export const buildTimeEventsForFillRegions = (
  allAnnotations,
  boundaryTimePoints,
) => {
  if (!allAnnotations || allAnnotations.length === 0) {
    return [];
  }

  const [minX, maxX] = boundaryTimePoints;
  const timeEvents = [];

  allAnnotations.forEach((annotation) => {
    const activatedTime = new Date(annotation.customerDBTimeInsert).getTime();
    const resolvedTime = annotation.resolveCustomerDBTime
      ? new Date(annotation.resolveCustomerDBTime).getTime()
      : null;

    // Check if annotation overlaps with the visible range
    const overlapsRange =
      activatedTime <= maxX && (!resolvedTime || resolvedTime >= minX);

    if (!overlapsRange) {
      return; // Skip annotations that don't overlap
    }

    // Clamp start time to visible window
    const startTime = activatedTime < minX ? minX : activatedTime;

    // Only add start event if it's within or before the visible range
    if (activatedTime <= maxX) {
      timeEvents.push({
        time: startTime,
        type: "start",
        annotation: annotation,
      });
    }

    // Add end event if resolved, clamped to visible window
    if (resolvedTime) {
      const endTime = resolvedTime > maxX ? maxX : resolvedTime;

      // Only add end event if it's within or after the visible range
      if (resolvedTime >= minX) {
        timeEvents.push({
          time: endTime,
          type: "end",
          annotation: annotation,
        });
      }
    }
  });

  timeEvents.sort((a, b) => a.time - b.time);
  return timeEvents;
};

/**
 * Build time events from grouped visible time points for severity fill regions
 * This ensures fill regions align with grouped annotation markers
 * @param {Array} visibleTimePoints - Grouped time points from getVisibleTimePointsForZoom
 * @param {Array} boundaryTimePoints - [minX, maxX] of the visible range
 * @param {Array} allAnnotations - All annotations (for handling spanning annotations)
 * @returns {Array} Time events with grouped timestamps, sorted by time
 */
export const buildTimeEventsFromGroupedPoints = (
  visibleTimePoints,
  boundaryTimePoints,
  allAnnotations,
) => {
  const [minX, maxX] = boundaryTimePoints;
  const timeEvents = [];

  // Track annotations we've processed via visible time points
  const processedStarts = new Set();
  const processedEnds = new Set();

  // Build events from grouped time points (using grouped timestamps)
  visibleTimePoints.forEach((tp) => {
    tp.startAnnotations?.forEach((annotation) => {
      const id =
        annotation.incidentID ||
        `${annotation.customerDBTimeInsert}-${annotation.incidentErrorName}`;
      processedStarts.add(id);
      timeEvents.push({
        time: tp.timestamp,
        type: "start",
        annotation,
      });
    });

    tp.endAnnotations?.forEach((annotation) => {
      const id =
        annotation.incidentID ||
        `${annotation.customerDBTimeInsert}-${annotation.incidentErrorName}`;
      processedEnds.add(id);
      timeEvents.push({
        time: tp.timestamp,
        type: "end",
        annotation,
      });
    });
  });

  // Handle annotations whose start/end times fall outside the visible range
  // but still affect the fill regions (spanning annotations)
  allAnnotations?.forEach((annotation) => {
    const id =
      annotation.incidentID ||
      `${annotation.customerDBTimeInsert}-${annotation.incidentErrorName}`;
    const activatedTime = new Date(annotation.customerDBTimeInsert).getTime();
    const resolvedTime = annotation.resolveCustomerDBTime
      ? new Date(annotation.resolveCustomerDBTime).getTime()
      : null;

    // Check if annotation overlaps with the visible range
    const overlapsRange =
      activatedTime <= maxX && (!resolvedTime || resolvedTime >= minX);

    if (!overlapsRange) {
      return;
    }

    // If start is before visible range and we haven't processed a start event
    if (activatedTime < minX && !processedStarts.has(id)) {
      timeEvents.push({
        time: minX, // Clamp to start of visible range
        type: "start",
        annotation,
      });
      processedStarts.add(id);
    }

    // If end is after visible range and we haven't processed an end event
    if (resolvedTime && resolvedTime > maxX && !processedEnds.has(id)) {
      timeEvents.push({
        time: maxX, // Clamp to end of visible range
        type: "end",
        annotation,
      });
      processedEnds.add(id);
    }
  });

  timeEvents.sort((a, b) => a.time - b.time);
  return timeEvents;
};

/**
 * Build time events from annotations with clamped timestamps for severity intervals
 * @param {Array} filteredAnnotations - Pre-filtered annotations
 * @param {Object} zoomedRange - { minX, maxX } zoom range
 * @returns {Array} Time events with clamped timestamps, sorted by time
 */
export const buildTimeEventsWithClamping = (
  filteredAnnotations,
  zoomedRange,
) => {
  const timeEvents = [];

  filteredAnnotations.forEach((annotation) => {
    const activatedTime = new Date(annotation.customerDBTimeInsert).getTime();
    const resolvedTime = annotation.resolveCustomerDBTime
      ? new Date(annotation.resolveCustomerDBTime).getTime()
      : null;

    // Clamp start time to zoom window
    const startTime =
      zoomedRange && zoomedRange.minX && activatedTime < zoomedRange.minX
        ? zoomedRange.minX
        : activatedTime;

    timeEvents.push({
      time: startTime,
      type: "start",
      annotation: annotation,
    });

    // Add end event if resolved, clamped to zoom window
    if (resolvedTime) {
      const endTime =
        zoomedRange && zoomedRange.maxX && resolvedTime > zoomedRange.maxX
          ? zoomedRange.maxX
          : resolvedTime;

      timeEvents.push({
        time: endTime,
        type: "end",
        annotation: annotation,
      });
    }
  });

  timeEvents.sort((a, b) => a.time - b.time);
  return timeEvents;
};

/**
 * Generate series data for chart including invisible annotations series
 * @param {Array} series - Original chart series
 * @param {Array} metrics - Metrics configuration
 * @param {Object} startDate - { value: dateString }
 * @returns {Array} Series array with annotations series appended
 */
export const getSeriesForChart = (series, metrics, startDate) => {
  const hasY1 = metrics.some((metric) => metric.yaxis === "Y1");
  const hasY2 = metrics.some((metric) => metric.yaxis === "Y2");

  // Create an invisible series for the annotations axis
  // Need at least one data point to make the y-axis render, but outside visible range
  const baseDate = new Date(startDate?.value || new Date());
  const minTime = baseDate.getTime();

  const annotationsSeries = {
    data: [
      { x: minTime, y: null }, // null value won't show in tooltip
    ],
  };

  if (!hasY1 && !hasY2) {
    return [{ data: [] }, { data: [] }, annotationsSeries];
  }
  if (!hasY1 || !hasY2) {
    return [...series, { data: [] }, annotationsSeries];
  }
  return [...series, annotationsSeries];
};

/**
 * Get active events at a specific timestamp for tooltip display
 * @param {Array} annotationData - Array of event/alert annotations
 * @param {number} hoverXaxis - Timestamp of the hovered point
 * @returns {Array} Array of active events at the hovered timestamp
 */
export const getActiveEventsAtTimestamp = (annotationData, hoverXaxis) => {
  const activeEvents = [];
  if (annotationData && annotationData.length > 0 && hoverXaxis) {
    annotationData.forEach((event) => {
      const startTime = event.customerDBTimeInsert
        ? new Date(event.customerDBTimeInsert).getTime()
        : null;
      const endTime = event.resolveCustomerDBTime
        ? new Date(event.resolveCustomerDBTime).getTime()
        : null;

      // Event is active if:
      // 1. It has started (startTime <= hoverXaxis)
      // 2. AND either not resolved yet (endTime is null) OR resolved after hover time (endTime >= hoverXaxis)
      if (startTime && startTime <= hoverXaxis) {
        if (!endTime || endTime >= hoverXaxis) {
          activeEvents.push(event);
        }
      }
    });
  }
  return activeEvents;
};

/**
 * Generate HTML for active events section in tooltip
 * @param {Array} activeEvents - Array of active events
 * @param {Object} alertTagStyles - Severity color styles from DataFile
 * @returns {string} HTML string for active events display
 */
export const generateActiveEventsHtml = (activeEvents, alertTagStyles) => {
  if (!activeEvents || activeEvents.length === 0) {
    return "";
  }
  const items = [];
  activeEvents.forEach((event) => {
    const severityKey = event.severity?.toLowerCase();
    const severityStyle =
      alertTagStyles.severity[severityKey] || alertTagStyles.severity.default;

    const severityColor = severityStyle.fontColor;
    const src = getAnnotationSourceDisplay(event.source);

    items.push(`
      <div>
        <span style="font-size: 11px;">
          ${
            event.severity
              ? `<span style="color: ${severityColor}; font-weight: 600;">(${event.severity}) </span>`
              : ""
          }
          <strong>${event.incidentErrorName || src.label}</strong>
          <span style="display:inline-block; background:${src.bg}; color:${src.color}; font-weight:600; font-size:10px; padding:1px 6px; border-radius:500px; margin-left:6px;">${src.label}</span>
          ${
            event.entityAffectedDisplayID
              ? `<br/><span style="color: #6B7280;">Entity Affected: ${event.entityAffectedDisplayID}</span>`
              : ""
          }
        </span>
      </div>
    `);
  });

  return items.join("");
};

/**
 * Generate tooltip HTML for chart data points with active events
 * @param {Object} opts - ApexCharts tooltip options (series, seriesIndex, dataPointIndex, w)
 * @param {Array} metrics - Array of metric configurations
 * @param {Function} t - Translation function
 * @param {Array} visibleSeries - Array indicating which series are visible
 * @param {Array} annotationData - Array of event/alert annotations
 * @param {Object} alertTagStyles - Severity color styles from DataFile
 * @param {Function} normalizeKey - Function to normalize keys for translation
 * @returns {string} HTML string for tooltip display
 */
export const getTooltipHtml = (
  { series, seriesIndex, dataPointIndex, w },
  metrics,
  t,
  visibleSeries = [],
  annotationData = [],
  alertTagStyles,
  normalizeKey,
  batteryInfo = {},
  selectedEntity = null,
) => {
  const metricNames = metrics.map((metric) => metric.name);
  const metricByName = metrics.reduce((acc, metric) => {
    acc[metric.name] = metric.name;
    return acc;
  }, {});
  let title = "No Title";
  let hoverItems = [];
  let tooltipItemContainerClass = "tooltip-item-container";
  const hoverXaxis = w.globals.seriesX[seriesIndex][dataPointIndex];
  let currentSeriesDataPoint =
    w?.config?.series[seriesIndex]["data"][dataPointIndex];

  if (currentSeriesDataPoint && currentSeriesDataPoint?.tooltipTitle) {
    title = currentSeriesDataPoint?.tooltipTitle;
  }

  const hoverIndexes = w.globals.seriesX.map((seriesX) => {
    return seriesX.findIndex((xData) => xData === hoverXaxis);
  });

  hoverIndexes.forEach((hoverIndex, seriesEachIndex) => {
    let seriesName = w.globals.seriesNames[seriesEachIndex];
    if (!metricNames.includes(seriesName)) {
      return;
    }
    if (visibleSeries.length && !visibleSeries[seriesEachIndex]) {
      return;
    }

    let value = series[seriesEachIndex][hoverIndex];
    if (value == undefined) {
      return;
    }

    let currentSeries = w.config.series[seriesEachIndex];
    const seriesColor = w.globals.colors[seriesEachIndex] || "#4D44D8";
    const metricForSeries =
      metricByName[seriesName] ||
      metrics.find((metric) => metric.key === currentSeries?.key);
    const unit = metricForSeries?.unit ?? currentSeries?.unit ?? "";

    seriesName = t(
      `digital_twin:metrics.${normalizeKey(seriesName)}`,
      seriesName,
    );

    value = unit ? `${value} ${unit}` : value;
    const valueMappings = currentSeries["data"][hoverIndex]["valueMappings"];

    if (valueMappings) {
      let key = String(value).trim().toLowerCase();
      value = valueMappings[key] ?? value;
    }

    if (
      currentSeries["data"][hoverIndex] &&
      currentSeries["data"][hoverIndex]["xAxisToolTipLabel"]
    ) {
      value = currentSeries["data"][hoverIndex]["xAxisToolTipLabel"];
    }

    hoverItems.push(`
      <div class="${tooltipItemContainerClass}">
        <span class="dot" style="background-color: ${seriesColor};"></span>
        <span style = "color #444B51;" class="tooltip-item-text">
          ${seriesName}: <strong>${value ?? "NA"}</strong>
        </span>
      </div>
    `);
  });

  let valuesToAppend = currentSeriesDataPoint?.valuesToAppend || {};

  Object.entries(valuesToAppend).forEach(([key, value]) => {
    hoverItems.push(
      `<div class="${tooltipItemContainerClass}">
        <span class="dot" style=" display: none; background-color: ${"#FF1493"};"></span>
        <span class="tooltip-item-text">
          ${key}: <strong>${value ?? "NA"}</strong>
        </span>
      </div>`,
    );
  });

  // Find and display active events at the hovered timestamp
  const activeEvents = getActiveEventsAtTimestamp(annotationData, hoverXaxis);
  const activeEventsHtml = generateActiveEventsHtml(
    activeEvents,
    alertTagStyles,
  );
  if (activeEventsHtml) {
    hoverItems.push(activeEventsHtml);
  }

  let hoverListHtml = hoverItems.join("");

  return `
    <div>
      <span>
        <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px">
          ${title}
        </div>
      </span>
    </div>
    <div class="tooltip-container">
      ${hoverListHtml}
    </div>
  </div>`;
};
